import styled from "@emotion/styled";
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  Paper as MuiPaper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import GlobalStyle from "components/GlobalStyle";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { WhatsNewDialog } from "pages/news";
import { spacing } from "@mui/system";
import { Layout as LayoutIcon, Search, List } from "react-feather";
import { SidebarItemsType } from "types/sidebar";
import { User } from "core/types/user.types";
import { connect } from "react-redux";
import { Dispatch, RootState } from "core/store/store.types";
import { authSelectors } from "core/store/modules/auth";
import { Header } from "containers/app/header";
import { Sidebar } from "containers/app/sidebar";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import { Instagram } from "react-feather";
import { NewResearch } from "pages/new-research";
import { useTranslation } from "react-i18next";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import { Footer } from "components/atoms/footer";
import { ActAsBar } from "components/organisms/act-as-bar";

const drawerWidth = 260;

const Root = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};
  overflow-y: auto;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

interface OwnProps {
  children: React.ReactNode;
  handleLogout: () => void;
}

interface StateProps {
  user?: User;
}

interface DispatchProps {}

export type Props = OwnProps & StateProps & DispatchProps;

const Layout = ({ children, handleLogout, user }: Props) => {
  const [t] = useTranslation();
  const isSuspended = user?.billingInfo?.billingStatus === "suspended";
  const [openModal, setOpenModal] = useState(false);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [sidebarItems, setSidebarItems] = useState<SidebarItemsType[]>([]);

  useEffect(() => {
    const items = [
      {
        href: "/web-rep",
        icon: LayoutIcon,
        title: t("drawer-dashboard.web-reputation"),
        children:
          user?.role === "admin"
            ? [
                {
                  href: "/web-rep/dashboard",
                  icon: TuneRoundedIcon,
                  title: t("drawer-dashboard.dashboard"),
                },
                {
                  href: "/web-rep/searches",
                  icon: Search,
                  title: t("drawer-dashboard.searches"),
                },
                {
                  // Ricerche di tutti i clienti -> pratica intestata al cliente. Solo admin,
                  // subito dopo "Le mie ricerche" (le ricerche dell'account con cui si e' entrati).
                  href: "/web-rep/client-searches",
                  icon: ManageSearchRoundedIcon,
                  title: t("drawer-dashboard.client-searches"),
                },
                // {
                //   href: "/web-rep/reports",
                //   icon: List,
                //   title: t("drawer-dashboard.reports"),
                // },
                // {
                //   href: "/web-rep/favourite-urls",
                //   icon: StarBorderRoundedIcon,
                //   title: t("drawer-dashboard.favourite-urls"),
                // },
                {
                  href: "/web-rep/practices",
                  icon: FilePresentIcon,
                  title: t("drawer-dashboard.practice"),
                },
                {
                  href: "/web-rep/domains",
                  icon: AlternateEmailRoundedIcon,
                  title: t("drawer-dashboard.domains"),
                },
                {
                  href: "/web-rep/takedown",
                  icon: GavelRoundedIcon,
                  title: t("drawer-dashboard.takedown"),
                },
                {
                  href: "/web-rep/admin",
                  icon: SupervisorAccountIcon,
                  title: t("drawer-dashboard.admin"),
                },
                {
                  href: "/web-rep/admin/usage",
                  icon: List,
                  title: t("drawer-dashboard.usage-log"),
                },
                {
                  href: "/web-rep/configuration",
                  icon: SettingsIcon,
                  title: t("drawer-dashboard.configuration"),
                },
              ]
            : user?.role === "legals"
            ? [
                {
                  href: "/web-rep/dashboard",
                  icon: TuneRoundedIcon,
                  title: t("drawer-dashboard.dashboard"),
                },
                {
                  href: "/web-rep/searches",
                  icon: Search,
                  title: t("drawer-dashboard.searches"),
                },
                // {
                //   href: "/web-rep/reports",
                //   icon: List,
                //   title: t("drawer-dashboard.reports"),
                // },
                // {
                //   href: "/web-rep/favourite-urls",
                //   icon: StarBorderRoundedIcon,
                //   title: t("drawer-dashboard.favourite-urls"),
                // },
                {
                  href: "/web-rep/practices",
                  icon: FilePresentIcon,
                  title: t("drawer-dashboard.practice"),
                },
                {
                  href: "/web-rep/domains",
                  icon: AlternateEmailRoundedIcon,
                  title: t("drawer-dashboard.domains"),
                },
                {
                  href: "/web-rep/takedown",
                  icon: GavelRoundedIcon,
                  title: t("drawer-dashboard.takedown"),
                },
                {
                  href: "/web-rep/configuration",
                  icon: SettingsIcon,
                  title: t("drawer-dashboard.configuration"),
                },
              ]
            : [
                {
                  href: "/web-rep/dashboard",
                  icon: TuneRoundedIcon,
                  title: t("drawer-dashboard.dashboard"),
                },
                {
                  href: "/web-rep/searches",
                  icon: Search,
                  title: t("drawer-dashboard.searches"),
                },
                // {
                //   href: "/web-rep/reports",
                //   icon: List,
                //   title: t("drawer-dashboard.reports"),
                // },
                // {
                //   href: "/web-rep/favourite-urls",
                //   icon: StarBorderRoundedIcon,
                //   title: t("drawer-dashboard.favourite-urls"),
                // },
                {
                  href: "/web-rep/practices",
                  icon: FilePresentIcon,
                  title: t("drawer-dashboard.practice"),
                },
                {
                  href: "/web-rep/removals",
                  icon: GavelRoundedIcon,
                  title: t("drawer-dashboard.my-removals"),
                },
                {
                  href: "/web-rep/configuration",
                  icon: SettingsIcon,
                  title: t("drawer-dashboard.configuration"),
                },
              ],
      },
      {
        href: "/social",
        icon: PhoneIphoneRoundedIcon,
        title: t("drawer-dashboard.social"),
        children: [
          {
            href: "/social/my-social-reports",
            icon: AutoGraphRoundedIcon,
            title: t("drawer-dashboard.reports"),
          },
          {
            href: "/social/instagram-account",
            icon: Instagram,
            title: t("drawer-dashboard.account"),
          },
        ],
      },
    ];
    setSidebarItems([...items]);
  }, [user, t]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={open}
            onClose={handleDrawerToggle}
            items={[{ title: "", pages: sidebarItems }]}
            user={user}
            showFooter={user ? true : false}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            items={[{ title: "", pages: sidebarItems }]}
            user={user}
            showFooter={user ? true : false}
          />
        </Box>
      </Drawer>
      <AppContent
        style={{
          maxWidth:
            isLgUp || isMdUp ? `calc(100vw - ${drawerWidth}px)` : "100%",
        }}
      >
        <Header
          onSignOut={handleLogout}
          onDrawerToggle={handleDrawerToggle}
          onButtonClick={() => {
            if (!isSuspended) setOpenModal(true);
          }}
        />
        {/* Sessione "lavora per conto di": si disegna da solo come banner permanente
            quando la delega e' attiva, e come comando per aprirla quando non lo e'.
            Invisibile ai clienti. Montato qui perche' il layout e' l'unico punto
            attraversato da tutte le pagine dopo il login. */}
        <ActAsBar />
        {isSuspended && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              borderRadius: 0,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <span>
                <strong>{t("suspension.title")}</strong> —{" "}
                {t("suspension.message")}
              </span>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                href="/my-profile"
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {t("suspension.cta")}
              </Button>
            </Box>
          </Alert>
        )}
        <MainContent p={isLgUp ? 12 : 5}>
          {children}
          <Outlet />
        </MainContent>
        <Footer />
      </AppContent>
      <NewResearch isVisible={openModal} setIsVisible={setOpenModal} />
      {/* Annuncio "cosa e' cambiato": compare da solo al primo accesso dopo un rilascio e poi
          non torna piu'. Montato qui perche' il layout e' l'unico punto attraversato da tutte
          le pagine dopo il login. Si rilegge dalla pagina Novita'. */}
      <WhatsNewDialog />
    </Root>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    user: authSelectors.getUser(state),
  }),
  (dispatch: Dispatch) => ({})
)(Layout);
